import React, { useEffect, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  getCurrentSubscription,
  getPlanHistory,
  cancelSubscription,
  revokeScheduledCancellation,
  renewSubscription,
  verifyPayment,
} from "store/actions/subscriptionAction"
import { showAlert } from "store/reducers/alertReducer"
import { ROUTES } from "constants/routes"
import {
  getCancellationEligibility,
  getRenewalEligibility,
  resolvePaymentUrl,
  RENEWAL_WINDOW_DAYS,
} from "helpers/billingRenewal"
import "./billing.scss"

const BILLING_RENEW_SUBSCRIPTION_KEY = "billingRenewSubscriptionId"
const BILLING_RENEW_INVOICE_KEY = "billingRenewInvoiceId"

const statusColors = {
  active: "badge-active",
  expired: "badge-expired",
  cancelled: "badge-cancelled",
  inactive: "badge-pending",
  suspended: "badge-pending",
  pending: "badge-pending",
}

const CANCEL_REASONS = [
  { id: "too-expensive", label: "Too expensive" },
  { id: "not-using-enough", label: "Not using it enough" },
  { id: "missing-features", label: "Missing features" },
  { id: "switching-competitor", label: "Switching to a competitor" },
  { id: "other", label: "Other" },
]

const BillingDashboard = () => {
  const { t } = useTranslation()
  document.title = t("Billing Dashboard ")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { subscriptionLoading, currentSubscription, actionLoading, planHistoryLoading, planHistory } =
    useSelector(state => state.Subscription)

  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState("")
  const [renewRedirecting, setRenewRedirecting] = useState(false)
  const [verifyingPayment, setVerifyingPayment] = useState(false)

  useEffect(() => {
    dispatch(getCurrentSubscription({ forBilling: true }))
    dispatch(getPlanHistory({ perPage: 20, pageNo: 1 }))
  }, [dispatch])

  const sub = currentSubscription
  const subscriptionPlan = sub?.plan || sub?.planId || null
  const subscriptionStatus = String(sub?.status || "").toLowerCase()
  const getBillingCycleLabel = () => {
    const rawCycle =
      sub?.billingCycle ||
      sub?.metadata?.billingCycle ||
      subscriptionPlan?.billingCycle ||
      subscriptionPlan?.metadata?.billingCycle

    if (!rawCycle) return t("Monthly")

    const normalized = String(rawCycle).trim().toLowerCase()
    if (normalized === "annual" || normalized === "yearly" || normalized === "year") {
      return t("Yearly")
    }
    if (normalized === "monthly" || normalized === "month") {
      return t("Monthly")
    }
    return t(normalized.charAt(0).toUpperCase() + normalized.slice(1))
  }

  const renewalEligibility = getRenewalEligibility(sub, subscriptionPlan)
  const cancellationEligibility = getCancellationEligibility(sub)
  const canCancel = cancellationEligibility.eligible
  const days = renewalEligibility.daysUntilExpiry
  const canRenew = renewalEligibility.eligible
  const isCancelled = subscriptionStatus === "cancelled"
  const cancelScheduled = Boolean(sub?.cancelAtPeriodEnd)
  const autoRenewalEnabled =
    Boolean(sub?.autoRenewal) &&
    !cancelScheduled &&
    Boolean(sub?.tapPaymentAgreementId)
  const scheduledCancelEndDate =
    sub?.currentPeriodEnd || sub?.expiresAt || sub?.endDate || null
  const showExpiryWarning =
    days !== null && days > 0 && days <= 7 && subscriptionStatus === "active"

  const handleRenewalSuccess = () => {
    sessionStorage.removeItem(BILLING_RENEW_SUBSCRIPTION_KEY)
    sessionStorage.removeItem(BILLING_RENEW_INVOICE_KEY)
    dispatch(
      showAlert({
        text: t("Subscription renewed successfully."),
        type: "success",
      })
    )
    dispatch(getCurrentSubscription({ forBilling: true }))
    dispatch(getPlanHistory({ perPage: 20, pageNo: 1 }))
  }

  const runRenewalVerification = async paymentId => {
    setVerifyingPayment(true)
    try {
      const action = await dispatch(
        verifyPayment({
          paymentId,
          subscriptionId:
            sessionStorage.getItem(BILLING_RENEW_SUBSCRIPTION_KEY) || sub?._id || "",
          invoiceId: sessionStorage.getItem(BILLING_RENEW_INVOICE_KEY) || "",
          gateway: "tap",
        })
      )
      const { status, message } = action.payload || {}
      if (status) {
        handleRenewalSuccess()
      } else {
        dispatch(
          showAlert({
            text: message || t("Payment verification failed."),
            type: "error",
          })
        )
      }
    } catch {
      dispatch(
        showAlert({
          text: t("Payment verification failed."),
          type: "error",
        })
      )
    } finally {
      setVerifyingPayment(false)
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const paymentId = params.get("tap_id") || params.get("paymentId")
    if (paymentId) {
      runRenewalVerification(paymentId)
    }
  }, [location.search])

  const handleCancel = () => {
    if (!cancelReason) {
      dispatch(
        showAlert({ text: t("Please select a cancellation reason."), type: "error" })
      )
      return
    }
    if (!sub?._id) {
      dispatch(showAlert({ text: t("Subscription id is missing."), type: "error" }))
      return
    }
    if (!canCancel) {
      dispatch(
        showAlert({
          text: t("Only an active subscription can be cancelled."),
          type: "error",
        })
      )
      return
    }
    dispatch(cancelSubscription({ id: sub._id, reason: cancelReason })).then(action => {
      if (cancelSubscription.rejected.match(action)) {
        dispatch(
          showAlert({
            text: t("Failed to cancel subscription."),
            type: "error",
          })
        )
        return
      }
      const { status, message, data } = action.payload || {}
      if (status) {
        const endDate = formatDate(
          data?.currentPeriodEnd || scheduledCancelEndDate
        )
        dispatch(
          showAlert({
            text: t(
              "Cancellation scheduled. You keep access until {{date}}.",
              { date: endDate }
            ),
            type: "success",
          })
        )
        dispatch(getCurrentSubscription({ forBilling: true }))
        dispatch(getPlanHistory({ perPage: 20, pageNo: 1 }))
      } else {
        dispatch(
          showAlert({
            text: message || t("Failed to cancel subscription."),
            type: "error",
          })
        )
      }
      setShowCancelModal(false)
      setCancelReason("")
    })
  }

  const handleRevokeCancel = () => {
    if (!sub?._id) return
    dispatch(revokeScheduledCancellation({ id: sub._id })).then(action => {
      if (revokeScheduledCancellation.rejected.match(action)) {
        dispatch(
          showAlert({
            text: t("Failed to resume subscription."),
            type: "error",
          })
        )
        return
      }
      const { status, message } = action.payload || {}
      if (status) {
        dispatch(
          showAlert({
            text: t("Scheduled cancellation has been revoked."),
            type: "success",
          })
        )
        dispatch(getPlanHistory({ perPage: 20, pageNo: 1 }))
      } else {
        dispatch(
          showAlert({
            text: message || t("Failed to resume subscription."),
            type: "error",
          })
        )
      }
    })
  }

  const handleRenew = () => {
    if (!sub?._id) {
      dispatch(showAlert({ text: t("Subscription id is missing."), type: "error" }))
      return
    }
    if (!canRenew) {
      dispatch(
        showAlert({
          text: t(
            "Renewal is available within {{days}} days of your subscription expiry date.",
            { days: RENEWAL_WINDOW_DAYS }
          ),
          type: "error",
        })
      )
      return
    }
    dispatch(renewSubscription({ id: sub._id })).then(action => {
      const { status, message, data } = action.payload || {}
      if (!status) {
        dispatch(
          showAlert({
            text: message || t("Failed to renew subscription."),
            type: "error",
          })
        )
        return
      }
      if (data?.subscription?._id) {
        sessionStorage.setItem(
          BILLING_RENEW_SUBSCRIPTION_KEY,
          String(data.subscription._id)
        )
      }
      if (data?.invoice?._id) {
        sessionStorage.setItem(BILLING_RENEW_INVOICE_KEY, String(data.invoice._id))
      }
      if (data?.freePlan) {
        handleRenewalSuccess()
        return
      }
      if (data?.autoCharged) {
        handleRenewalSuccess()
        return
      }
      const paymentUrl = resolvePaymentUrl(data)
      if (paymentUrl) {
        setRenewRedirecting(true)
        window.location.href = paymentUrl
        return
      }
      const chargeId = data?.session?.id
      if (chargeId) {
        runRenewalVerification(String(chargeId))
        return
      }
      dispatch(
        showAlert({
          text: t("Unable to start Tap payment. Please try again."),
          type: "error",
        })
      )
    })
  }

  const formatDate = dateStr => {
    if (!dateStr) return "-"
    return new Date(dateStr).toLocaleDateString()
  }

  const getFromDate = item =>
    item?.details?.periodStart ||
    item?.details?.fromDate ||
    item?.subscriptionPeriodStart ||
    item?.subscriptionId?.currentPeriodStart ||
    null

  const getToDate = item =>
    item?.details?.periodEnd ||
    item?.details?.toDate ||
    item?.subscriptionPeriodEnd ||
    item?.subscriptionId?.currentPeriodEnd ||
    null

  const getEventLabel = event => {
    const labels = {
      "subscription.created": t("Subscription Created"),
      "subscription.renewed": t("Subscription Renewed"),
      "subscription.cancelled": t("Subscription Cancelled"),
      "subscription.cancel_scheduled": t("Cancellation Scheduled"),
      "subscription.cancel_revoked": t("Cancellation Revoked"),
      "subscription.upgraded": t("Plan Upgraded"),
      "subscription.downgraded": t("Plan Downgraded"),
    }
    return labels[event] || event || "-"
  }

  const getSubscriptionRef = item => {
    if (!item) return "-"
    if (typeof item.subscriptionId === "string") return item.subscriptionId || "-"
    return item.subscriptionId?._id || "-"
  }

  const getPlanName = item =>
    item?.planName || item?.toPlan || item?.fromPlan || item?.newPlan?.name || item?.previousPlan?.name || "-"

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <h4 className="mb-4">{t("Billing Dashboard")}</h4>

          {cancelScheduled && !isCancelled && (
            <div className="alert alert-warning d-flex align-items-start gap-2 mb-4">
              <i className="bx bx-time-five fs-5 mt-1" />
              <div className="flex-grow-1">
                <strong>{t("Cancellation scheduled")}</strong>
                <p className="mb-2 small">
                  {t(
                    "Your subscription stays active until {{date}}. You can still use all features until then.",
                    { date: formatDate(scheduledCancelEndDate) }
                  )}
                </p>
                <Button
                  color="warning"
                  size="sm"
                  outline
                  onClick={handleRevokeCancel}
                  disabled={actionLoading}
                >
                  {t("Keep my subscription")}
                </Button>
              </div>
            </div>
          )}

          {isCancelled && (
            <div className="alert alert-secondary d-flex align-items-start gap-2 mb-4">
              <i className="bx bx-info-circle fs-5 mt-1" />
              <div>
                <strong>{t("Subscription cancelled")}</strong>
                <p className="mb-0 small">
                  {t(
                    "Your subscription is no longer active. You can subscribe again from the plans page."
                  )}
                </p>
              </div>
            </div>
          )}

          {(renewRedirecting || verifyingPayment) && (
            <div className="text-center py-3 mb-3">
              <Spinner color="primary" className="me-2" />
              {renewRedirecting
                ? t("Redirecting to Tap secure checkout...")
                : t("Processing payment...")}
            </div>
          )}

          {showExpiryWarning && canRenew && (
            <div className="expiry-warning">
              <i className="bx bx-error-circle" />
              <span>
                {t("Your subscription expires in")} <strong>{days}</strong>{" "}
                {days === 1 ? t("day") : t("days")}.{" "}
                <Button
                  color="link"
                  className="p-0 align-baseline text-warning fw-bold"
                  onClick={handleRenew}
                >
                  {t("Renew now")}
                </Button>
              </span>
            </div>
          )}

          {subscriptionLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : !sub ? (
            <Card>
              <CardBody className="text-center py-5">
                <i className="bx bx-package fs-1 text-muted d-block mb-3" />
                <h5>{t("No active subscription")}</h5>
                <p className="text-muted mb-4">
                  {t("Subscribe to a plan to unlock all features.")}
                </p>
                <Button
                  color="primary"
                  onClick={() => navigate(ROUTES.BILLING_PLANS)}
                >
                  {t("View Plans")}
                </Button>
              </CardBody>
            </Card>
          ) : (
            <>
              {/* Current subscription card */}
              <Row>
                <Col lg={8}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="mb-1">
                            {sub.planName || subscriptionPlan?.name || t("Subscription")}
                          </h5>
                          <p className="text-muted mb-0">
                            {subscriptionPlan?.description || ""}
                          </p>
                        </div>
                        <span
                          className={`badge rounded-pill ${
                            statusColors[subscriptionStatus] || "badge-pending"
                          } px-3 py-2`}
                        >
                          {sub.status || "-"}
                        </span>
                      </div>

                      <Row className="g-3">
                        <Col sm={6} md={3}>
                          <div className="text-muted small">{t("Started")}</div>
                          <div className="fw-semibold">
                            {formatDate(
                              sub.startedAt ||
                                sub.currentPeriodStart ||
                                sub.startDate ||
                                sub.createdAt
                            )}
                          </div>
                        </Col>
                        <Col sm={6} md={3}>
                          <div className="text-muted small">{t("Expires")}</div>
                          <div className="fw-semibold">
                            {formatDate(
                              sub.expiresAt || sub.currentPeriodEnd || sub.endDate
                            )}
                          </div>
                        </Col>
                        <Col sm={6} md={3}>
                          <div className="text-muted small">{t("Billing Cycle")}</div>
                          <div className="fw-semibold">{getBillingCycleLabel()}</div>
                        </Col>
                        <Col sm={6} md={3}>
                          <div className="text-muted small">{t("Amount")}</div>
                          <div className="fw-semibold">
                            {sub.currency || "SAR"}{" "}
                            {Number(
                              sub.amount || sub.planAmount || subscriptionPlan?.price || 0
                            ).toFixed(2)}
                          </div>
                        </Col>
                      </Row>

                      {autoRenewalEnabled && subscriptionStatus === "active" && (
                        <div className="alert alert-info py-2 px-3 mt-3 mb-0 small">
                          <i className="bx bx-refresh me-1" />
                          {t(
                            "Auto-renewal is enabled. Your subscription will renew automatically at the end of each billing period using your saved Tap payment method."
                          )}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                {/* Actions card */}
                <Col lg={4}>
                  <Card className="h-100">
                    <CardBody className="d-flex flex-column gap-2">
                      <h6 className="text-muted mb-2">{t("Actions")}</h6>

                      <Button
                        color="primary"
                        outline
                        block
                        onClick={() => navigate(ROUTES.BILLING_PLANS)}
                      >
                        <i className="bx bx-transfer me-1" />
                        {t("Upgrade / Change Plan")}
                      </Button>

                      {canRenew && !autoRenewalEnabled && (
                        <Button
                          color="success"
                          outline
                          block
                          onClick={handleRenew}
                          disabled={
                            actionLoading || renewRedirecting || verifyingPayment
                          }
                        >
                          <i className="bx bx-refresh me-1" />
                          {t("Renew Subscription")}
                        </Button>
                      )}

                      {canRenew && autoRenewalEnabled && (
                        <p className="text-muted small mb-0">
                          {t(
                            "Your plan renews automatically. Use manual renewal only if auto-renewal failed."
                          )}
                        </p>
                      )}

                      {canRenew && autoRenewalEnabled && (
                        <Button
                          color="success"
                          outline
                          block
                          onClick={handleRenew}
                          disabled={
                            actionLoading || renewRedirecting || verifyingPayment
                          }
                        >
                          <i className="bx bx-refresh me-1" />
                          {t("Renew Manually")}
                        </Button>
                      )}

                      {subscriptionStatus === "active" &&
                        renewalEligibility.reason === "not_due_yet" &&
                        days !== null && (
                          <p className="text-muted small mb-0">
                            {t(
                              "Renewal opens {{days}} days before your subscription expires ({{remaining}} days remaining).",
                              {
                                days: RENEWAL_WINDOW_DAYS,
                                remaining: days,
                              }
                            )}
                          </p>
                        )}

                      {subscriptionStatus === "cancelled" && (
                        <Button
                          color="primary"
                          outline
                          block
                          onClick={() => navigate(ROUTES.BILLING_PLANS)}
                        >
                          <i className="bx bx-package me-1" />
                          {t("Subscribe Again")}
                        </Button>
                      )}

                      <Button
                        color="primary"
                        outline
                        block
                        onClick={() => navigate(ROUTES.BILLING_INVOICES)}
                      >
                        <i className="bx bx-receipt me-1" />
                        {t("View Invoices")}
                      </Button>

                      {canCancel && (
                        <Button
                          color="danger"
                          outline
                          block
                          onClick={() => {
                            setCancelReason("")
                            setShowCancelModal(true)
                          }}
                          disabled={actionLoading}
                        >
                          <i className="bx bx-x-circle me-1" />
                          {t("Cancel Subscription")}
                        </Button>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Plan features */}
              {Array.isArray(subscriptionPlan?.features) &&
                subscriptionPlan.features.length > 0 && (
                  <Card>
                    <CardBody>
                      <h6 className="mb-3">{t("Plan Features")}</h6>
                      <Row>
                        {subscriptionPlan.features.map((feature, idx) => (
                          <Col key={idx} md={4} className="mb-2">
                            <i className="bx bx-check-circle text-success me-2" />
                            {typeof feature === "string"
                              ? feature
                              : feature.label || feature.name || ""}
                          </Col>
                        ))}
                      </Row>
                    </CardBody>
                  </Card>
                )}

              <Card>
                <CardBody>
                  <h6 className="mb-3">{t("Plan History")}</h6>
                  {planHistoryLoading ? (
                    <div className="text-center py-3">
                      <Spinner size="sm" color="primary" />
                    </div>
                  ) : !Array.isArray(planHistory) || planHistory.length === 0 ? (
                    <p className="text-muted mb-0">{t("No plan history found.")}</p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th>{t("Date")}</th>
                            <th>{t("Subscription ID")}</th>
                            <th>{t("Plan Name")}</th>
                            <th>{t("From Date")}</th>
                            <th>{t("To Date")}</th>
                            <th>{t("Event")}</th>
                            <th>{t("From Plan")}</th>
                            <th>{t("To Plan")}</th>
                            <th>{t("Status")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {planHistory.map(item => (
                            <tr key={item._id}>
                              <td>{formatDate(item.createdAt)}</td>
                              <td className="text-break">{getSubscriptionRef(item)}</td>
                              <td>{getPlanName(item)}</td>
                              <td>{formatDate(getFromDate(item))}</td>
                              <td>{formatDate(getToDate(item))}</td>
                              <td>{getEventLabel(item.event)}</td>
                              <td>{item.fromPlan || item.previousPlan?.name || "-"}</td>
                              <td>{item.toPlan || item.newPlan?.name || "-"}</td>
                              <td>
                                <Badge color="light" className="text-dark">
                                  {item.status || "-"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      </div>

      {/* Cancel Subscription Modal */}
      <Modal
        isOpen={showCancelModal}
        toggle={() => setShowCancelModal(false)}
        centered
      >
        <ModalHeader toggle={() => setShowCancelModal(false)}>
          {t("Cancel Subscription")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            {t(
              "Your subscription will remain active until the end of your current billing period{{date}}. Access ends after that date; no further charges will be made.",
              {
                date: scheduledCancelEndDate
                  ? ` (${formatDate(scheduledCancelEndDate)})`
                  : "",
              }
            )}
          </p>
          <p>{t("We're sorry to see you go. Please tell us why you're leaving:")}</p>
          {CANCEL_REASONS.map(({ id, label }) => (
            <div
              key={id}
              className={`form-check mb-2 cancel-reason-option${
                cancelReason === label ? " selected" : ""
              }`}
              onClick={() => setCancelReason(label)}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  setCancelReason(label)
                }
              }}
              role="button"
              tabIndex={0}
            >
              <Input
                type="radio"
                name="cancelReason"
                id={`cancel-reason-${id}`}
                className="form-check-input"
                value={label}
                checked={cancelReason === label}
                onChange={event => setCancelReason(event.target.value)}
                onClick={event => event.stopPropagation()}
              />
              <Label className="form-check-label" for={`cancel-reason-${id}`}>
                {t(label)}
              </Label>
            </div>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowCancelModal(false)}>
            {t("Keep Subscription")}
          </Button>
          <Button
            color="danger"
            onClick={handleCancel}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t("Cancelling...")}
              </>
            ) : (
              t("Confirm Cancellation")
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default BillingDashboard
